import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth, API } from '../../App';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { EyeOff, Save } from 'lucide-react';

const LABELS = {
  rides: 'Rides', past_rides: 'Past Rides', users: 'Users', attendance: 'Attendance',
  partners: 'Partners', pricing: 'Pricing', vehicles: 'Vehicles', expenses: 'Expenses',
  petrol: 'Petrol', car_wash: 'Car Wash', financials: 'Financials', debts: 'Company Debts',
  daily_vouchers: 'Daily Vouchers', statistics: 'Statistics', partner_reports: 'Partner Reports',
  backup: 'Data Backup', integrations: 'Integrations',
  payments: 'Payments', invoices: 'Invoices', summary: 'Summary',
};

const GROUP_TITLES = { admin: 'Admin menu', driver: 'Driver menu', partner: 'Partner portal' };

export const SectionVisibility = () => {
  const { token } = useAuth();
  const [hideable, setHideable] = useState({ admin: [], driver: [], partner: [] });
  const [hidden, setHidden] = useState({ admin: [], driver: [], partner: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/hidden-sections`).then(r => {
      setHideable(r.data.hideable || { admin: [], driver: [], partner: [] });
      setHidden({ admin: r.data.admin || [], driver: r.data.driver || [], partner: r.data.partner || [] });
    }).catch(() => {});
  }, []);

  const toggle = (role, key) => {
    setHidden(prev => ({
      ...prev,
      [role]: prev[role].includes(key) ? prev[role].filter(k => k !== key) : [...prev[role], key],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/hidden-sections`, hidden, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Section visibility saved - hidden sections disappear from menus');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save section visibility');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-blue-200 rounded-sm shadow-sm p-6" data-testid="section-visibility-card">
      <div className="flex items-center gap-2 mb-1">
        <EyeOff className="h-5 w-5 text-slate-700" />
        <h2 className="text-lg font-semibold text-black">Section Visibility (Super Admin)</h2>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Hide unused sections globally: checked sections disappear from menus for ALL users of that account type.
      </p>
      <div className="space-y-5">
        {['admin', 'driver', 'partner'].map(role => (
          <div key={role}>
            <h3 className="text-sm font-semibold text-slate-700 mb-2">{GROUP_TITLES[role]}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {(hideable[role] || []).map(key => (
                <label
                  key={`${role}-${key}`}
                  className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded px-2 py-1.5 cursor-pointer hover:bg-slate-100"
                  data-testid={`hide-${role}-${key}`}
                >
                  <Checkbox
                    checked={hidden[role]?.includes(key)}
                    onCheckedChange={() => toggle(role, key)}
                  />
                  <span className={hidden[role]?.includes(key) ? 'line-through text-slate-400' : ''}>
                    {LABELS[key] || key}
                  </span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      <Button onClick={save} disabled={saving} className="mt-5 bg-blue-900 hover:bg-blue-800" data-testid="save-section-visibility">
        <Save className="h-4 w-4 mr-2" />
        {saving ? 'Saving...' : 'Save Visibility'}
      </Button>
    </div>
  );
};

export default SectionVisibility;
